import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatEventParts } from '@/lib/eventTime';
import { resolveMediaUrl } from '@/utils/mediaUrl';

// Shown under a registration confirmation: the next thing happening, if it's
// soon. Built while Cindy's list was registering for the free PB Talent workshop
// and Ronen was the following day — the moment right after someone registers is
// the one time you know for certain they're paying attention.
//
// Deliberately not a second pitch: no price, no "buy", no urgency. Someone who
// registered thirty seconds ago has already said yes to one thing today.
//
// It picks the next event itself rather than hardcoding one, and only shows
// something inside WINDOW_HOURS, so it switches off on its own after the event
// passes and is right for the next one without anybody remembering to change it.

const WINDOW_HOURS = 48;

type Session = {
  productId: string;
  title: string;
  date: string;
  host?: string | null;
  guest?: string | null;
  // host/guest as people, with headshots — who's on is most of why the next one
  // is worth a click.
  hosts?: { name: string; role: string | null; image: string | null }[];
  guests?: { name: string; role: string | null; image: string | null }[];
  summary?: string | null;
  past?: boolean;
};

// "tomorrow" / "today" / "Thursday", whichever is true when the page loads.
function whenWord(d: Date): string {
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / 86400000);
  if (days <= 0) return 'later today';
  if (days === 1) return 'tomorrow';
  return `on ${d.toLocaleDateString('en-US', { weekday: 'long' })}`;
}

export function NextEventCrossSell({ excludeProductId }: { excludeProductId?: string }) {
  const [next, setNext] = useState<Session | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/marketplace/webinar-calendar')
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.sessions) return;
        const now = Date.now();
        const cutoff = now + WINDOW_HOURS * 3600 * 1000;
        const soonest = (data.sessions as Session[])
          .filter(s => !s.past
            && String(s.productId) !== String(excludeProductId)
            && new Date(s.date).getTime() > now
            && new Date(s.date).getTime() <= cutoff)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
        setNext(soonest || null);
      })
      .catch(() => { /* a cross-sell is never worth an error */ });
    return () => { cancelled = true; };
  }, [excludeProductId]);

  if (!next) return null;

  const d = new Date(next.date);
  const parts = formatEventParts(next.date, 'short');
  // Standing host plus whoever is booked for that date, falling back to the flat
  // names on a session the calendar hasn't got lists for.
  const people = [...(next.hosts || []), ...(next.guests || [])].length
    ? [...(next.hosts || []), ...(next.guests || [])]
    : [next.host, next.guest].filter(Boolean).map(n => ({ name: String(n), role: null, image: null }));
  const faces = people.filter(h => h.image).slice(0, 3);

  return (
    <div
      style={{
        marginTop: 10,
        padding: '14px 16px',
        borderRadius: 'var(--hj-r-sm)',
        background: 'var(--hj-surface-2)',
        border: '1px solid var(--hj-hairline)',
      }}
    >
      <div className="mono-label" style={{ fontSize: 10.5, color: 'var(--hj-ink-3)', marginBottom: 7 }}>
        ALSO, COMING UP {whenWord(d).toUpperCase()}
      </div>
      <Link
        to={`/course/${next.productId}`}
        style={{ display: 'block', fontSize: 15.5, fontWeight: 700, color: 'var(--hj-ink)', textDecoration: 'none', lineHeight: 1.25 }}
      >
        {next.title}
      </Link>
      <div style={{ marginTop: 4, fontSize: 13, color: 'var(--hj-ink-2)' }}>
        {parts?.full} · {parts?.time}
      </div>
      {people.length > 0 && (
        <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
          {faces.length > 0 && (
            <span className="flex flex-shrink-0">
              {faces.map((h, i) => (
                <img
                  key={`${h.name}-${i}`}
                  src={resolveMediaUrl(h.image!)}
                  alt=""
                  className="hj-headshot"
                  style={{ width: 28, height: 28, marginLeft: i ? -7 : 0, boxShadow: '0 0 0 2px var(--hj-bg)' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ))}
            </span>
          )}
          <span style={{ fontSize: 13, color: 'var(--hj-ink-2)', minWidth: 0 }}>
            with {people.map(h => h.name).join(' & ')}
          </span>
        </div>
      )}
      {next.summary && (
        <p className="m-0" style={{ marginTop: 7, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.45 }}>
          {next.summary}
        </p>
      )}
      <Link
        to={`/course/${next.productId}`}
        style={{ display: 'inline-block', marginTop: 10, fontSize: 13.5, fontWeight: 700, color: 'var(--hj-ink)' }}
      >
        Take a look →
      </Link>
    </div>
  );
}
